<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="users"
        :row-key="'id'"
        :loading="loading"
        :pagination="paginationConfig"
        :scroll="{ x: 1300 }"
        class="text-nowrap align-middle people-table"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ t("user_page.no_users_found") }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'avatar'">
            <div class="d-flex justify-content-center">
              <img :src="getUserAvatar(record.avatar)" :alt="t('user_page.avatar')" class="user-avatar" />
            </div>
          </template>

          <template v-else-if="column && column.key === 'name'">
            {{ `${record.first_name || ""} ${record.last_name || ""}`.trim() || t("user_page.not_available_short") }}
          </template>

          <template v-else-if="column && column.key === 'role'">
            {{ formatRole(record.role_permission_id) }}
          </template>

          <template v-else-if="column && column.key === 'warehouse'">
            {{ record.warehouse_name || record.warehouse_code || t("user_page.not_available_short") }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="isActive(record) ? 'status-active' : 'status-inactive'">
              {{ isActive(record) ? t("user_page.status_active") : t("user_page.status_inactive") }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2 table-actions">
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('user_page.view')"
                data-bs-toggle="modal"
                data-bs-target="#userDetailsModal"
                @click="$emit('view-user', record)"
              >
                <img src="../../../assets/img/icons/eye.svg" :alt="t('user_page.view')" style="width: 14px" />
              </button>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('user_page.edit')"
                data-bs-toggle="modal"
                data-bs-target="#userCreateModal"
                @click="$emit('edit-user', record)"
              >
                <img src="../../../assets/img/icons/edit.svg" :alt="t('user_page.edit')" style="width: 14px" />
              </button>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('user_page.delete')"
                @click="$emit('delete-user', record)"
              >
                <img src="../../../assets/img/icons/close.svg" :alt="t('user_page.delete')" style="width: 14px" />
              </button>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { User } from "../../../services/user";
import type { RolePermission } from "../../../services/role_permission";
import { buildAssetUrl, buildStorageAssetUrl } from "../../../utils/settings";
import defaultAvatarImg from "../../../assets/img/users/user-default.jpg";

export default defineComponent({
  name: "UserList",
  props: {
    users: {
      type: Array as () => User[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    roles: {
      type: Array as () => RolePermission[],
      default: () => [],
    },
  },
  emits: ["view-user", "edit-user", "delete-user"],
  setup(props) {
    const { t } = useI18n();

    const columns = computed(() => [
      { title: t("user_page.image"), key: "avatar", align: "center" as const, width: 100 },
      { title: t("user_page.full_name"), key: "name", align: "center" as const, width: 220 },
      { title: t("user_page.username"), dataIndex: "username", key: "username", align: "center" as const, width: 170 },
      { title: t("user_page.email"), dataIndex: "email", key: "email", align: "center" as const, width: 220 },
      { title: t("user_page.phone"), dataIndex: "phone", key: "phone", align: "center" as const, width: 160 },
      { title: t("user_page.role"), key: "role", align: "center" as const, width: 160 },
      { title: t("user_page.warehouse"), key: "warehouse", align: "center" as const, width: 180 },
      { title: t("user_page.status"), key: "status", align: "center" as const, width: 140 },
      { title: t("user_page.action"), key: "actions", align: "center" as const, width: 120 },
    ]);

    const paginationConfig = computed(() => ({
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "30", "50", "100"],
      defaultPageSize: 10,
      locale: { items_per_page: t("user_page.items_per_page") },
    }));

    const isActive = (user: User) => Boolean(user.active || user.status === "active");

    const formatRole = (roleId?: number) => {
      if (!roleId) return t("user_page.not_available_short");
      const matchedRole = props.roles.find((role) => Number(role.id) === Number(roleId));
      const roleName = matchedRole?.role_name || "";
      const roleKeyMap: Record<string, string> = {
        "super admin": "user_page.role_super_admin",
        admin: "user_page.role_admin",
        manager: "user_page.role_manager",
        cashier: "user_page.role_cashier",
      };
      const normalizedRole = roleName.trim().toLowerCase();
      if (roleKeyMap[normalizedRole]) {
        return t(roleKeyMap[normalizedRole]);
      }
      return roleName || t("user_page.role_with_id", { id: roleId });
    };

    const getUserAvatar = (avatar?: string) => {
      if (!avatar) return defaultAvatarImg;
      if (String(avatar).startsWith("http")) return avatar;
      if (String(avatar).startsWith("uploads/")) return buildAssetUrl(String(avatar));
      return buildStorageAssetUrl(String(avatar));
    };

    return {
      t,
      columns,
      paginationConfig,
      isActive,
      formatRole,
      getUserAvatar,
    };
  },
});
</script>

<style scoped>
.people-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.people-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #eef2f7;
}

.table-actions {
  min-width: 72px;
}

.action-icon-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-active {
  color: #1f9254;
  background: #e8f7ee;
}

.status-inactive {
  color: #d33c43;
  background: #fdecec;
}
</style>
